import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import api from '../services/apiService';
import './LiftworkerImport.css';

const labels = {
  technicalDataOwner: 'Techn. Daten (Verm)', technicalDataManufacturer: 'Techn. Daten (Herst)',
  technicalDataSale: 'Techn. Daten (Verkauf)', userManual: 'Bedienungsanleitung', errorCodes: 'Fehlercodes',
  qsv1Url: 'QSV 01', qsv2Url: 'QSV 02', qsv3Url: 'QSV 03', picHP: 'Titelbild',
};

export function useLiftworkerImport(liftworkerId, articleId) {
  const [config, setConfig] = useState(null);
  const [busyField, setBusyField] = useState(null);
  const [notice, setNotice] = useState(null);
  const [picker, setPicker] = useState(null);
  const current = useRef(null);
  const request = useRef(null);
  const identity = `${articleId || 'new'}:${String(liftworkerId || '').trim()}`;
  current.current = identity;

  useEffect(() => {
    const controller = new AbortController();
    api.get('/liftworker-import/config', { signal: controller.signal }).then(({ data }) => {
      if (!controller.signal.aborted) setConfig(data);
    }).catch(() => {
      if (!controller.signal.aborted) setConfig({ enabled: false, reason: 'Der LW-Import ist derzeit nicht verfügbar.' });
    });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setPicker(null);
    setNotice(null);
    setBusyField(null);
    request.current?.abort();
    request.current = null;
    return () => request.current?.abort();
  }, [identity]);

  const reason = !config ? 'LW-Import wird geprüft.' : !config.enabled ? config.reason
    : !String(liftworkerId || '').trim() ? 'Bitte zuerst die Liftworker-ID im Artikel eintragen.' : '';

  function apply(document, field, onSelect, expectedIdentity) {
    if (current.current !== expectedIdentity) return;
    onSelect(document.url);
    setPicker(null);
    setNotice({ message: `${labels[field]} wurde übernommen. Zum dauerhaften Speichern bitte den Artikel speichern.` });
  }

  async function start(field, onSelect) {
    if (reason || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    const expectedIdentity = identity;
    setBusyField(field);
    setNotice(null);
    setPicker(null);
    try {
      const { data } = await api.get('/liftworker-import/documents', {
        params: { liftworkerId: String(liftworkerId).trim(), field }, signal: controller.signal,
      });
      if (controller.signal.aborted || current.current !== expectedIdentity) return;
      if (!data.documents.length) {
        setNotice({ message: `Für ${labels[field]} sind in Liftworker keine passenden Unterlagen hinterlegt. Der vorhandene Wert bleibt erhalten.` });
      } else if (data.documents.length === 1) {
        apply(data.documents[0], field, onSelect, expectedIdentity);
      } else {
        setPicker({ documents: data.documents, field, onSelect, identity: expectedIdentity });
      }
    } catch (error) {
      if (!controller.signal.aborted && current.current === expectedIdentity) {
        setNotice({ error: true, message: error.response?.data?.error || 'Der LW-Import ist fehlgeschlagen. Bitte erneut versuchen.' });
      }
    } finally {
      if (request.current === controller) { request.current = null; setBusyField(null); }
    }
  }

  return { reason, busyField, notice, picker, start, close: () => setPicker(null),
    choose: document => picker && apply(document, picker.field, picker.onSelect, picker.identity) };
}

export function LiftworkerImportButton({ control, field, onSelect }) {
  return <button type="button" className="lw-import-button"
    disabled={!!control.reason || !!control.busyField}
    title={control.reason || `${labels[field]} aus Liftworker übernehmen`}
    aria-label={`LW Import: ${labels[field]}`}
    onClick={() => control.start(field, onSelect)}>
    {control.busyField === field ? 'Lädt …' : 'LW Import'}
  </button>;
}

export function LiftworkerImportFeedback({ control }) {
  if (control.notice) return <p className={`lw-import-notice${control.notice.error ? ' lw-import-error' : ''}`}
    role={control.notice.error ? 'alert' : 'status'}>{control.notice.message}</p>;
  return control.reason ? <p className="lw-import-hint">{control.reason}</p> : null;
}

export function LiftworkerDocumentPicker({ control }) {
  const picker = control.picker;
  const appElement = document.getElementById('root');
  return <Modal isOpen={!!picker} onRequestClose={control.close}
    appElement={appElement || undefined} ariaHideApp={!!appElement}
    className="lw-import-dialog" overlayClassName="lw-import-overlay"
    aria={{ labelledby: 'lw-import-title' }}>
    <div className="lw-import-heading">
      <h2 id="lw-import-title">{picker ? labels[picker.field] : 'Dokument'} auswählen</h2>
      <button type="button" onClick={control.close} aria-label="Dokumentauswahl schließen">Schließen</button>
    </div>
    <p>Mehrere passende Unterlagen sind vorhanden. Wähle die Datei, deren URL in das Feld übernommen werden soll.</p>
    <ul className="lw-import-documents">
      {picker?.documents.map(doc => <li key={doc.id}>
        <div className="lw-import-document-details">
          <strong>{doc.name}</strong>
          <span>{[doc.type, doc.language || 'Sprache nicht angegeben', doc.source].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="lw-import-document-actions">
          <a href={doc.url} target="_blank" rel="noopener noreferrer">Öffnen</a>
          <button type="button" onClick={() => control.choose(doc)}>Übernehmen</button>
        </div>
      </li>)}
    </ul>
  </Modal>;
}
